import { useIntl } from 'react-intl';
import { Button, Checkbox, Divider, Input, TextArea } from '../../../../ui';
import { useState } from 'react';
import useQueryApiClient from '../../../../utils/useQueryApiClient';
import { FarmType } from '../../../../interfaces/BackendModels';
import { StyledBusinessTab } from '../style';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import { WithoutModalAddressFinder } from 'ui/AdressSearch/WithoutModal';
import { Form } from 'antd';

interface Options {
  label: string;
  value: number;
}

interface BaseInformationProps {
  isAdmin?: boolean;
  onAddPvdNumberClick?: () => void;
  publicAllowed?: boolean;
}
export const BaseInformation = ({
  isAdmin = false,
  onAddPvdNumberClick,
  publicAllowed = false,
}: BaseInformationProps) => {
  const [options, setOptions] = useState<Options[]>([]);
  const intl = useIntl();
  const form = useFormInstance();
  const isPublicProfile = Form.useWatch('public', form);
  const pvdNr: string | undefined = Form.useWatch('pvd_nr', form);
  const registeredFullAddress = Form.useWatch('registered_full_address', form);
  const registeredAddress = Form.useWatch('registered_address', form);

  useQueryApiClient({
    request: {
      url: '/api/v2/farm-types',
    },
    onSuccess: (response) => handleOptionsParse(response),
  });

  const handleOptionsParse = (options: FarmType[]) => {
    const parsedOptions: Options[] = options
      .map((option: FarmType) => ({
        label: option.name ?? '',
        value: option.id ?? 0,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    setOptions(parsedOptions);
  };

  return (
    <StyledBusinessTab>
      <div className={'content'}>
        <div className={'section'}>
          <div className={'title'}>
            <h3>{intl.formatMessage({ id: 'navigation.base_information' })}</h3>
          </div>
          <Input
            label={intl.formatMessage({ id: 'company.name' })}
            layout={'vertical'}
            name={'name'}
            className={'label-fix'}
            disabled
          />

          <div className={'double-input'}>
            <Input
              label={intl.formatMessage({ id: 'company.reg_nr' })}
              layout={'vertical'}
              name={'reg_nr'}
              className={'label-fix'}
              disabled
            />

            {!!registeredFullAddress && !!registeredAddress ? (
              <Input
                label={intl.formatMessage({ id: 'company.registered_address' })}
                layout={'vertical'}
                name={'registered_address'}
                className={'label-fix'}
                disabled
              />
            ) : (
              <WithoutModalAddressFinder
                form={form}
                name={'registered_address'}
                label={intl.formatMessage({ id: 'company.registered_address' })}
                layout="vertical"
                disabled={false}
              />
            )}
          </div>

          <div className={'double-checkbox'}>
            <Checkbox
              name={'public'}
              label={intl.formatMessage({ id: 'company.public' })}
              disabled={!publicAllowed}
            />

            <Checkbox
              name={'registered_address_is_actual_address'}
              label={intl.formatMessage({
                id: 'company.reg_address_is_actual_address',
              })}
            />
          </div>

          <div className={'double-input'}>
            <Input
              label={intl.formatMessage({ id: 'company.pvn_number' })}
              layout={'vertical'}
              name={'pvn_nr'}
              className={'label-fix'}
              disabled
            />

            <Input
              label={intl.formatMessage({ id: 'company.pvd_number' })}
              layout={'vertical'}
              name={'pvd_nr'}
              disabled
              className={'label-fix'}
            />
          </div>
          {!pvdNr && (
            <div className={'add-pvd-number-row'}>
              <Button
                label={intl.formatMessage({ id: 'company.add_pvd_number' })}
                type={'primary'}
                className={'new-btn'}
                onClick={onAddPvdNumberClick}
              />
            </div>
          )}
        </div>
        <Divider />
        <div className={'section'}>
          <div className={'title'}>
            <h3>
              {intl.formatMessage({ id: 'general.additional_information' })}
            </h3>
          </div>
          <Input
            label={intl.formatMessage({ id: 'general.brand_name' })}
            name={'brand'}
            validations={!isAdmin && isPublicProfile ? ['required'] : []}
            dependencies={['public']}
            className={'label-fix'}
          />
          <TextArea
            label={intl.formatMessage({
              id: 'general.short_company_description',
            })}
            className={'text-area'}
            maxLength={250}
            showCount
            name={'short_description'}
            validations={!isAdmin && isPublicProfile ? ['required'] : []}
          />
          <TextArea
            label={intl.formatMessage({
              id: 'general.company_description',
            })}
            maxLength={3000}
            className={'text-area'}
            name={'description'}
            validations={!isAdmin && isPublicProfile ? ['required'] : []}
          />

          <div className={'double-input'}>
            <Input
              label={intl.formatMessage({ id: 'general.phone' })}
              className={'label-fix'}
              validations={
                !isAdmin ? ['phoneNumber', 'required'] : ['phoneNumber']
              }
              name={'contact_phone'}
            />
            <Input
              label={intl.formatMessage({ id: 'general.email' })}
              className={'label-fix'}
              validations={!isAdmin ? ['email', 'required'] : ['email']}
              name={'contact_email'}
            />
          </div>
          <div className={'double-input'}>
            <Input
              label={intl.formatMessage({ id: 'general.website' })}
              className={'label-fix'}
              name={'website'}
            />
          </div>
        </div>
        <Divider />
        <div className={'section'}>
          <div className={'title'}>
            <h3>{intl.formatMessage({ id: 'general.company_type' })}</h3>
          </div>
          <div className={'double-column-checkbox'}>
            <Checkbox options={options} name={'types'} />
          </div>
        </div>
      </div>
    </StyledBusinessTab>
  );
};
